// @flow
import React from 'react'
import styled, { css } from 'styled-components'
import { Input, Checkbox } from 'antd'
import { fontSize, spacing } from 'frontend_core/dist/styles/variables'
import { mediaMax, mediaMin } from 'frontend_core/dist/styles/media'
import Colors from 'components/styles/colors'
import Button from 'components/atoms/Button'
import Title from 'components/atoms/Title'
import LinkText from 'components/atoms/LinkText'
import Icon from 'components/atoms/Icon'
import Description from 'components/atoms/Description'
import FloatBottom from 'components/molecules/FloatBottom'
import ReputationStar from 'components/molecules/ReputationStar'
import InputContainer from 'components/molecules/InputContainer'
import FormItem from 'components/molecules/FormItem'
import CheckboxList from 'components/molecules/CheckboxList'
import FormSectionTitle from 'components/molecules/FormSectionTitle'
import HeadlineReservation from 'components/molecules/HeadlineReservation'
import SelectPayMethodSection from 'components/organisms/SelectPayMethodSection'
import PointCouponSection from 'components/organisms/PointCouponSection'
import MainContentsWithSidebarInbox from 'components/templates/MainContentsWithSidebarInbox'
import BillStatement, {
  type PropTypes as BillStatementProps,
} from 'components/molecules/BillStatement'
import ReservationAdditionalFeeBlock, {
  type PropTypes as ReservationAdditionalFeeBlockProps,
} from 'components/molecules/ReservationAdditionalFeeBlock'
import SMConst from 'frontend_core/dist/constants/sm-const'

const Section = styled.div`
  margin-top: ${spacing.large}px;
`
const TitleDesc = styled.pre`
  margin-top: ${spacing.small_3}px;
  color: ${Colors.gray9};
  font-size: ${fontSize.small_2}px;
  line-height: 1.424;
  white-space: pre-wrap;
`
const H2 = styled.h2`
  font-size: ${fontSize.medium}px;
  font-weight: bold;
  ${mediaMin.phone`
    font-size: ${fontSize.medium_2}px;
  `};
`
const SubSection = styled.div`
  margin-top: ${spacing.small_3}px;
  border-top: 1px solid ${Colors.grayE};
`
const LinkTextWrapper = styled.div`
  margin-top: ${spacing.small_3}px;
`
const SectionSubTitleWrapper = styled.div`
  margin-top: ${spacing.medium_2}px;
`
const SectionSubTitle = styled.p`
  font-size: ${fontSize.small_3}px;
  font-weight: bold;
`
const ItemWrapper = styled.div`
  margin-top: ${spacing.small_3}px;
`
const FormWrapper = styled.div`
  margin-top: ${spacing.medium}px;
`
const CheckboxListWrapper = styled.div`
  margin-top: ${spacing.medium_2}px;
`
const ButtonWrapper = styled.span`
  display: inline-block;
  width: 50%;

  &:first-of-type {
    padding-right: ${spacing.small}px;
  }

  &:nth-of-type(2n) {
    padding-left: ${spacing.small}px;
  }

  ${mediaMin.phone`
    width: auto;
  `};
`
const AttentionWrapper = styled.div`
  margin-top: ${spacing.large}px;
`
const AttentionTitle = styled.p`
  font-size: ${fontSize.medium}px;
`
const AttentionBlock = styled.div`
  margin-top: ${spacing.medium}px;
`
const SectionDesc = styled.p`
  margin-top: ${spacing.small_2}px;
  font-size: ${fontSize.small_2}px;
  color: ${Colors.gray9};
`
const RadioListRecommendedWrapper = styled.div`
  margin-top: ${spacing.medium_3}px;
`
const FormSectionWrapper = styled.div`
  margin-top: ${spacing.medium_3}px;

  :first-of-type {
    margin-top: ${spacing.medium}px;
  }

  ${mediaMin.phone`
    margin-top: ${spacing.large}px;
    :first-child {
      margin-top: ${spacing.large}px;
    }
  `};
`
const ButtonColumnReverse = styled.div`
  ${props =>
    props.isActive &&
    css`
      ${mediaMax.phone`
      display: flex;
      flex-direction: column-reverse;
    `};
    `};
`
const ButtonItem = styled.div`
  ${props =>
    props.isReverse &&
    css`
      ${mediaMin.phone`
      display: inline-block;
      width: 197px;
    `};
      &:not(:first-child) {
        margin: 0 0 ${spacing.small_2}px 0;
        ${mediaMin.phone`
        margin: 0 0 0 ${spacing.small_2}px;
      `};
      }
    `};
  ${props =>
    !props.isReverse &&
    css`
      width: calc((100% - 8px) / 2);
      display: inline-block;

      &:not(:first-child) {
        margin-left: ${spacing.small_2}px;
      }

      ${mediaMin.phone`
      width: 160px;
      font-size: ${fontSize.small_3}px;
    `};
    `};
`
const BillStatementSection = styled.div`
  padding-top: ${spacing.large}px;
  border-top: 1px solid ${Colors.grayE};
`

export type PropTypes = {
  sidebarInbox: React.Component<*>,
  isShowContentsSp: boolean,
  isCorp: boolean,
  optionListMonth: Array<{
    label: string,
    value: number | string,
  }>,
  optionListYear: Array<{
    label: string,
    value: number | string,
  }>,
  onChangeCardNumber: Function,
  cardNumber: Array<string>,
  demanded: string,
  availablepoint: string,
  holdPoint: string,
  availablePointLately: string,
  showPoint: boolean,
  applyPoint: string,
  submitButton: {
    onClick: Function,
    isDisabled?: ?boolean,
  },
  onClickBack: Function,
  onClickOpenCreditCardForm: Function,
  isOpenCreditCard?: boolean,
  billStatementProps: BillStatementProps,
  paymentMethod?: ?number,
  isOverPaidyAvailablePeriod?: ?boolean,
  isOverPaidyUpperLimitAmount?: ?boolean,
}

export default function(props: PropTypes) {
  const isPaidy = props.paymentMethod === SMConst.PaymentMethod.Paidy
  const applyButtonDisable =
    isPaidy &&
    (props.isOverPaidyAvailablePeriod || props.isOverPaidyUpperLimitAmount)
  const applyButtonText = isPaidy
    ? i18n.t('page.shared.l_00072')
    : i18n.t('page.shared.select')

  return (
    <MainContentsWithSidebarInbox
      isShowContentsSp={props.isShowContentsSp}
      sidebarInbox={props.sidebarInbox}
    >
      <HeadlineReservation
        title={i18n.t('page.dashboard.reservation.selectPayMethod.title')}
        href="#"
        onClick={e => {
          e.preventDefault()
          props.onClickBack()
        }}
        linkText={i18n.t('page.dashboard.reservation.backToMessage')}
      >
        {i18n.t('page.dashboard.reservation.selectPayMethod.desc')}
      </HeadlineReservation>
      <Section>
        <SelectPayMethodSection {...props} />
      </Section>
      <Section>
        <PointCouponSection {...props} />
      </Section>
      <BillStatementSection>
        <BillStatement {...props.billStatementProps} />
      </BillStatementSection>
      <FloatBottom textAlign="right" hasSidebar>
        <ButtonColumnReverse isActive={isPaidy}>
          <ButtonItem isReverse={isPaidy}>
            <Button
              buttonType="medium"
              colorType="gray"
              tag="button"
              fullWidthSp
              onClick={props.onClickBack}
            >
              {i18n.t('page.shared.cancel')}
            </Button>
          </ButtonItem>
          <ButtonItem isReverse={isPaidy}>
            <Button
              buttonType="medium"
              colorType="blue"
              tag="button"
              fullWidthSp
              onClick={props.onClickApply}
              disabled={applyButtonDisable}
            >
              {applyButtonText}
            </Button>
          </ButtonItem>
        </ButtonColumnReverse>
      </FloatBottom>
      <ReservationAdditionalFeeBlock
        {...props.reservationAdditionalPaidyProps}
      />
    </MainContentsWithSidebarInbox>
  )
}



// WEBPACK FOOTER //
// ./src/components/pages/SelectPaymentMethodPage/index.jsx